// Paso 4 — la última mirada antes de cursar.
//
// Los avisos se recuentan y se juntan AQUÍ porque en la tabla están repartidos
// entre setenta filas: un aviso que hay que ir a buscar fila a fila no avisa de
// nada. Es la última oportunidad de ver que tres líneas van sin EAN.
import { useTranslation } from 'react-i18next'
import type { ArchivoResumen, CabeceraBorrador, LineaBorrador } from '@/types'
import { resumenAvisos, totales } from '@/utils/pedidoDesdeArchivo'
import { cromado, etiquetaCls } from './cromado'

interface Props {
  dark: boolean
  cab: CabeceraBorrador
  lineas: LineaBorrador[]
  archivos: ArchivoResumen[]
  error: { codigo: string; valores?: Record<string, string | number> } | null
}

function Dato({ dark, etiqueta, valor, ancho }: {
  dark: boolean; etiqueta: string; valor: string; ancho?: boolean
}) {
  const c = cromado(dark)
  return (
    <div className={`px-3.5 py-2.5 rounded-xl border ${c.panel} ${c.panelBorde} ${ancho ? 'sm:col-span-2' : ''}`}>
      <p className={`${etiquetaCls} ${c.dimTxt}`}>{etiqueta}</p>
      <p className={`text-[12px] mt-1 whitespace-pre-line leading-relaxed ${c.mainTxt}`}>
        {valor || '—'}
      </p>
    </div>
  )
}

export function PasoConfirmar({ dark, cab, lineas, archivos, error }: Props) {
  const { t } = useTranslation()
  const c = cromado(dark)
  const total = totales(lineas)
  const avisos = resumenAvisos(lineas)

  const partes: string[] = []
  if (avisos.sinEan) partes.push(t('salesOrders.desdeArchivo.resumen.sinEan', { count: avisos.sinEan }))
  if (avisos.repetidas) partes.push(t('salesOrders.desdeArchivo.resumen.repetidas', { count: avisos.repetidas }))
  if (avisos.recuperadas) partes.push(t('salesOrders.desdeArchivo.resumen.recuperadas', { count: avisos.recuperadas }))
  if (avisos.redondeadas) partes.push(t('salesOrders.desdeArchivo.resumen.redondeadas', { count: avisos.redondeadas }))
  if (avisos.conValorPropio) partes.push(t('salesOrders.desdeArchivo.resumen.propias', { count: avisos.conValorPropio }))

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Dato dark={dark} etiqueta={t('salesOrders.desdeArchivo.cliente')}
              valor={`${cab.clienteNombre}\n${cab.clienteId}`} />
        <Dato dark={dark} etiqueta={t('salesOrders.desdeArchivo.empresaGrupo')}
              valor={`${cab.empresa.toUpperCase()} · ${t(`salesOrders.desdeArchivo.grupos.${cab.grupo}`)}`} />
        <Dato dark={dark} etiqueta={t('salesOrders.desdeArchivo.direccion')}
              valor={cab.direccionTexto} ancho />
        <Dato dark={dark} etiqueta={t('salesOrders.desdeArchivo.fecha')} valor={cab.fecha} />
        <Dato dark={dark} etiqueta={t('salesOrders.desdeArchivo.almacen')} valor={cab.almacen} />
        {cab.numeroPedidoCliente && (
          <Dato dark={dark} etiqueta={t('salesOrders.desdeArchivo.numeroCliente')}
                valor={cab.numeroPedidoCliente} />
        )}
        {cab.copia && (
          <Dato dark={dark} etiqueta={t('salesOrders.desdeArchivo.copia')} valor={cab.copia} />
        )}
        {cab.nota && (
          <Dato dark={dark} etiqueta={t('salesOrders.desdeArchivo.nota')} valor={cab.nota} ancho />
        )}
      </div>

      <div className={`flex items-baseline gap-3 flex-wrap px-4 py-3 rounded-xl border ${c.panel} ${c.panelBorde}`}>
        <span className={`text-[20px] font-semibold tabular-nums ${c.mainTxt}`}>{total.lineas}</span>
        <span className={`text-[11px] ${c.mutedTxt}`}>{t('salesOrders.desdeArchivo.lineas')}</span>
        <span className={c.dimTxt}>·</span>
        <span className={`text-[20px] font-semibold tabular-nums ${c.mainTxt}`}>
          {total.unidades.toLocaleString()}
        </span>
        <span className={`text-[11px] ${c.mutedTxt}`}>{t('salesOrders.desdeArchivo.unidades')}</span>
        <span
          className={`text-[10px] font-mono ml-auto truncate max-w-[18rem] ${c.dimTxt}`}
          title={archivos.map((a) => a.nombre).join(', ')}
        >
          {archivos.map((a) => a.nombre).join(' · ')}
        </span>
      </div>

      {partes.length > 0 && (
        <div className={`px-4 py-3 rounded-xl border ${
          dark ? 'bg-amber-500/[0.06] border-amber-500/20' : 'bg-amber-50 border-amber-200'
        }`}>
          <p className={`${etiquetaCls} mb-1 ${dark ? 'text-amber-400' : 'text-amber-700'}`}>
            {t('salesOrders.desdeArchivo.revisa')}
          </p>
          <p className={`text-[12px] leading-relaxed ${dark ? 'text-amber-200/90' : 'text-amber-900'}`}>
            {partes.join(' · ')}
          </p>
        </div>
      )}

      {error && (
        <div className={`px-4 py-3 rounded-xl border text-[12px] ${
          dark ? 'bg-red-500/[0.07] border-red-500/20 text-red-300'
               : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {t(`salesOrders.desdeArchivo.errores.${error.codigo}`, {
            ...error.valores,
            defaultValue: t('salesOrders.desdeArchivo.errores.generico'),
          })}
        </div>
      )}
    </div>
  )
}
